import React, { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { showToast } from "../../redux/actions/toastActions";
import { fetchWrapper } from "../../utils/API/api.ts";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";

const ScannedLeadsNotes = ({
  setTriggerNotes,
  triggerNotes,
  eventsId,
  singleLeadsData,
}) => {
  const [notes, setNotes] = useState({
    qualifiedAs: "",
    leadRating: 0,
    assignedTo: "",
    additionalNotes: "",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (singleLeadsData && triggerNotes) {
      setNotes({
        qualifiedAs: singleLeadsData?.qualifiedAs || "",
        leadRating: singleLeadsData?.leadRating || 0,
        assignedTo: singleLeadsData?.assignedTo || "",
        additionalNotes: singleLeadsData?.additionalNotes || "",
      });
    }
  }, [singleLeadsData, triggerNotes]);

  const addNotes = async () => {
    setLoading(true);
    try {
      const response = await fetchWrapper(
        `${process.env.REACT_APP_SERVER_URL_LEADS}/attendee/addScannedLeadNotes?eventId=${eventsId}&leadId=${singleLeadsData?._id}`,
        {
          method: "POST",
          body: JSON.stringify({
            notes: notes,
          }),
        }
      );

      const data = response?.data;
      if (data) {
        queryClient.invalidateQueries(["leadsCount", eventsId]);
        queryClient.invalidateQueries(["leads", eventsId]);
        // fetch(
        //   `/attendee/getLeadDetails?eventId=${eventsId}&filter=${currentLead}`
        // );
      }
      setLoading(false);
      setTriggerNotes(false);
      navigate(`/attendee/${eventsId}?tab=leads`);
      if (response.status !== 200) {
        setLoading(false);
        throw new Error();
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
      dispatch(
        showToast({
          message: "Error",
          category: "danger",
        })
      );
    }
  };

  return (
    <AnimatePresence>
      {triggerNotes && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setTriggerNotes(false)}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <div
              className="
              bg-white w-full h-full md:h-[80%] md:w-[500px]
              md:rounded-2xl shadow-xl flex flex-col overflow-hidden
            "
            >
              <div className="flex justify-between items-center p-4 border-b flex-shrink-0 bg-white z-10">
                <h2 className="text-[20px] font-semibold">Notes</h2>
                <button onClick={() => setTriggerNotes(false)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 18 18"
                    className="fill-current text-gray-700"
                  >
                    <path d="M0 0h18v18H0z" fill="none" />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.293 9l4.646-4.646a1 1 0 10-1.414-1.414L9 7.586 4.354 2.94a1 1 0 10-1.414 1.414L7.586 9l-4.646 4.646a1 1 0 001.414 1.414L9 10.414l4.646 4.646a1 1 0 001.414-1.414L10.414 9z"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 pb-24">
                <section className="mt-4 border-b pb-5">
                  <p className="font-medium text-[16px]">
                    Qualify this lead as
                  </p>
                  <div className="flex justify-between mt-3">
                    {["hot", "warm", "cold"].map((status) => (
                      <div
                        key={status}
                        className={`border rounded-[10px] p-2 text-[15px] w-[32%] text-center cursor-pointer capitalize transition
                          ${
                            notes?.qualifiedAs === status
                              ? "bg-purple-100 border-primary font-medium"
                              : "text-gray-800 border-gray-300"
                          }`}
                        onClick={() => {
                          setNotes((prev) => ({
                            ...prev,
                            qualifiedAs:
                              prev.qualifiedAs === status ? "" : status,
                          }));
                        }}
                      >
                        {status}
                      </div>
                    ))}
                  </div>
                </section>

                {/* Rating */}
                <section className="mt-4 border-b pb-5">
                  <p className="font-medium text-[16px]">Lead Rating</p>
                  <div className="flex items-center gap-2 mt-3">
                    {[...Array(5)].map((_, index) => {
                      const filled = notes?.leadRating >= index + 1;
                      return (
                        <svg
                          key={index}
                          onClick={() => {
                            setNotes((prev) => ({
                              ...prev,
                              leadRating:
                                prev.leadRating === index + 1 ? 0 : index + 1,
                            }));
                          }}
                          width={28}
                          viewBox="0 0 32 32"
                          fill={filled ? "#e1c50e" : "#7a7a7a"}
                          className="cursor-pointer transition-transform hover:scale-110"
                        >
                          <path
                            d="M168,903.21 L160.571,907.375 L161.989,898.971 L155.594,892.442 L164.245,891.317 L168,883.313 L171.722,891.317 L180.344,892.54 L174.011,899.002 L175.335,907.406 L168,903.21 Z"
                            transform="translate(-152 -879)"
                          />
                        </svg>
                      );
                    })}
                  </div>
                </section>

                {/* Assigned To */}
                <section className="mt-4 border-b pb-5">
                  <p className="font-medium text-[16px]">Assigned To</p>
                  <input
                    type="text"
                    placeholder="Enter team member's name"
                    className="mt-3 w-full bg-[#F4F6F9] border-[2px] border-gray-200 rounded-lg h-[45px] px-3 text-[15px] focus:ring-transparent focus:border-primary focus:outline-none"
                    value={notes?.assignedTo}
                    maxLength={40}
                    onChange={(e) =>
                      setNotes({ ...notes, assignedTo: e.target.value })
                    }
                  />
                </section>

                <section className="mt-4 border-b pb-5">
                  <p className="font-medium text-[16px]">Additional Notes</p>
                  <textarea
                    rows={3}
                    placeholder="Add any additional notes about this lead..."
                    maxLength={300}
                    className="mt-3 w-full bg-[#F4F6F9] border-[2px] border-gray-200 rounded-lg p-2.5 text-[15px] focus:border-primary focus:outline-none focus:ring-transparent"
                    value={notes?.additionalNotes}
                    onChange={(e) =>
                      setNotes({ ...notes, additionalNotes: e.target.value })
                    }
                  />
                </section>
              </div>

              <div className="flex justify-between gap-2 p-4 border-t bg-white flex-shrink-0">
                <button
                  disabled={loading}
                  className="w-1/2 bg-white border border-primary text-primary rounded-[10px] h-[45px] font-medium"
                  onClick={() => setTriggerNotes(false)}
                >
                  Cancel
                </button>
                <button
                  disabled={loading}
                  className="w-1/2 bg-primary text-white rounded-[10px] h-[45px] font-medium"
                  onClick={addNotes}
                >
                  {loading ? "Saving..." : "Save Notes"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ScannedLeadsNotes;
